import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, CheckCircle2, ExternalLink, Settings, AlertTriangle } from 'lucide-react';
import IframeMyClaim from './IframeMyClaim';
import { 
  createOrUpdateContract, 
  getContract, 
  parseCalculationData,
  getMyClaimUrl,
  checkDatCredentials 
} from '../../services/dat/myClaimService';

const ContractForm = ({
  type = 'vro_calculation', // 'glass', 'vro_calculation', 'bundle_valuation'
  initialData = null,
  onContractCreated,
  onCalculationComplete,
  onSaved,
  onEvent,
  className = ''
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [calculationData, setCalculationData] = useState(null);
  const [credentialsConfigured, setCredentialsConfigured] = useState(null);
  const [checkingCredentials, setCheckingCredentials] = useState(true);

  // Contract type labels
  const typeLabels = {
    'glass': 'Glasberechnung',
    'vro_calculation': 'Schadenkalkulation',
    'bundle_valuation': 'Fahrzeugbewertung'
  };

  // Check credentials on mount
  useEffect(() => {
    const checkCredentials = async () => {
      setCheckingCredentials(true);
      try {
        const result = await checkDatCredentials();
        setCredentialsConfigured(result.configured);
        if (!result.configured) {
          setError('DAT-Zugangsdaten nicht konfiguriert. Bitte tragen Sie Ihre DAT-Credentials in den Admin-Einstellungen ein.');
        }
      } catch (err) {
        console.error('Error checking credentials:', err);
        setCredentialsConfigured(false);
      } finally {
        setCheckingCredentials(false);
      }
    };

    checkCredentials();
  }, []);

  // Create contract on mount or when type changes
  const initializeContract = useCallback(async () => {
    if (!credentialsConfigured) {
      setError('DAT-Zugangsdaten nicht konfiguriert. Bitte tragen Sie Ihre DAT-Credentials in den Admin-Einstellungen ein.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const contractData = {
        name: `Auftrag_${Date.now()}`,
        country: 'DE',
        language: 'de',
        currency: 'EUR',
        ...(initialData || {})
      };

      const newClaimId = await createOrUpdateContract(contractData, type);
      setClaimId(newClaimId);

      // Get myClaim URL with credentials
      const urlData = await getMyClaimUrl(
        initialData?.name || `Auftrag_${Date.now()}`,
        initialData?.vehicle || {}
      );
      
      if (urlData.myclaim_url) {
        setIframeUrl(urlData.myclaim_url);
      }

      // Notify parent
      if (onContractCreated) {
        onContractCreated(newClaimId, type);
      }
    } catch (err) {
      console.error('Error initializing contract:', err);
      setError(err.message || 'Fehler beim Erstellen des Auftrags');
    } finally {
      setLoading(false);
    }
  }, [type, initialData, onContractCreated, credentialsConfigured]);

  // Handle calculation complete from iframe
  const handleCalculationComplete = useCallback((data, id, contractType) => {
    const parsed = parseCalculationData(data);
    setCalculationData(parsed);

    if (onCalculationComplete) {
      onCalculationComplete(data, id, contractType);
    }
  }, [onCalculationComplete]);

  // Handle saved event
  const handleSaved = useCallback((data, id, contractType) => {
    if (onSaved) {
      onSaved(data, id, contractType);
    }
  }, [onSaved]);

  // Handle generic events
  const handleEvent = useCallback((eventName, eventData, id) => {
    if (onEvent) {
      onEvent(eventName, eventData, id);
    }
  }, [onEvent]);

  // Refresh calculation data
  const refreshCalculation = async () => {
    if (!claimId) return;

    try {
      const data = await getContract(claimId);
      const parsed = parseCalculationData(data);
      setCalculationData(parsed);
    } catch (err) {
      console.error('Error refreshing calculation:', err);
    }
  };

  // Show loading while checking credentials
  if (checkingCredentials) {
    return (
      <div className={`dat-contract-form ${className}`}>
        <div className="flex items-center justify-center py-12 bg-gray-50 rounded-xl border border-gray-100">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Prüfe DAT-Konfiguration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`dat-contract-form ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-800">
            {typeLabels[type] || 'DAT Kalkulation'}
          </h3>
          {claimId && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              ID: {claimId}
            </span>
          )}
        </div>
        {!loading && !claimId && credentialsConfigured && (
          <button
            onClick={initializeContract}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Kalkulation starten
          </button>
        )}
      </div>

      {/* Credentials Warning */}
      {!credentialsConfigured && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-amber-800 font-medium">DAT-Zugangsdaten nicht konfiguriert</p>
            <p className="text-amber-600 text-sm mt-1">
              Um die DAT-Kalkulation nutzen zu können, müssen zuerst die DAT-Zugangsdaten im Admin-Bereich unter "Einstellungen" hinterlegt werden.
            </p>
            <a 
              href="/einstellungen" 
              className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-amber-700 hover:text-amber-800"
            >
              <Settings className="w-4 h-4" />
              Zu den Einstellungen
            </a>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12 bg-gray-50 rounded-xl border border-gray-100">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Kalkulation wird vorbereitet...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && credentialsConfigured && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">Fehler</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
          <button
            onClick={initializeContract}
            className="ml-auto px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Calculation Summary */}
      {calculationData?.calculation && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">Kalkulation verfügbar</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Brutto</p>
              <p className="font-semibold text-gray-900">
                {calculationData.calculation.totalGross.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </p>
            </div>
            <div>
              <p className="text-gray-500">Netto</p>
              <p className="font-semibold text-gray-900">
                {calculationData.calculation.totalNet.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </p>
            </div>
            <div>
              <p className="text-gray-500">Ersatzteile</p>
              <p className="font-semibold text-gray-900">
                {calculationData.calculation.sparePartsTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </p>
            </div>
            <div>
              <p className="text-gray-500">Arbeitslohn</p>
              <p className="font-semibold text-gray-900">
                {calculationData.calculation.labourTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </p>
            </div>
          </div>
        </div>
      )}

      {/* iframe */}
      {iframeUrl && !loading && credentialsConfigured && (
        <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
          <IframeMyClaim
            url={iframeUrl}
            claimId={claimId}
            onCalculationComplete={handleCalculationComplete}
            onSaved={handleSaved}
            onEvent={handleEvent}
          />
        </div>
      )}
    </div>
  );
};

export default ContractForm;
